import { useEffect, useMemo, useRef, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { supabase } from "@/lib/supabase";
import { useDonationConfig } from "@/hooks/useDonationConfig";
import { formatEuro } from "@/lib/format";

const presetAmounts = [10, 20, 50, 100];

type DonationStatus =
  | "idle"
  | "creating"
  | "processing"
  | "success"
  | "error";

interface DonationPanelProps {
  actionId: string;
  slug: string;
}

export default function DonationPanel({ actionId, slug }: DonationPanelProps) {
  const { stripeEnabled, paypalEnabled } = useDonationConfig();
  const [selected, setSelected] = useState<number>(20);
  const [custom, setCustom] = useState("");
  const [status, setStatus] = useState<DonationStatus>("idle");
  const [message, setMessage] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<"card" | "paypal">("paypal");
  const [paypalClientId, setPaypalClientId] = useState<string | null | undefined>(
    (import.meta.env.VITE_PUBLIC_PAYPAL_CLIENT_ID as string) || undefined
  );
  const attemptRef = useRef("");

  useEffect(() => {
    let active = true;
    const envId = import.meta.env.VITE_PUBLIC_PAYPAL_CLIENT_ID as
      | string
      | undefined;
    supabase.functions
      .invoke("get-paypal-client-id")
      .then(({ data }) => {
        if (!active) return;
        if (data?.clientId) {
          setPaypalClientId(data.clientId as string);
        } else if (!envId) {
          setPaypalClientId(null);
        }
      })
      .catch(() => {
        if (active && !envId) setPaypalClientId(null);
      });
    return () => {
      active = false;
    };
  }, []);

  const amount = custom.trim() ? Math.max(0, Number(custom)) : selected;
  const validAmount = Number.isFinite(amount) && amount > 0;

  const paypalOptions = useMemo(() => {
    if (!paypalClientId) return null;
    return {
      "client-id": paypalClientId,
      currency: "EUR",
      intent: "capture" as const,
      components: "buttons",
    };
  }, [paypalClientId]);

  async function handleCard() {
    if (!validAmount) return;
    setStatus("creating");
    setMessage("");
    try {
      const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
      const pathPrefix = basePath ? `/${basePath}` : "";
      const origin = window.location.origin;
      const successUrl = `${origin}${pathPrefix}/actions/${slug}?donation=success&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${origin}${pathPrefix}/actions/${slug}?donation=cancel`;

      const { data, error } = await supabase.functions.invoke("donate-stripe", {
        body: { amount, actionId, successUrl, cancelUrl },
      });
      if (error) throw new Error(error.message || "Erreur inconnue.");
      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      throw new Error("Impossible de créer la session de paiement.");
    } catch (e) {
      setStatus("error");
      setMessage(
        e instanceof Error ? e.message : "Une erreur est survenue. Réessayez."
      );
    }
  }

  async function handleCreateOrder(): Promise<string> {
    if (!validAmount) throw new Error("Montant invalide.");
    setStatus("creating");
    setMessage("");
    attemptRef.current = crypto.randomUUID();
    const { data, error } = await supabase.functions.invoke(
      "create-paypal-order",
      {
        body: { attemptId: attemptRef.current, amount, actionId },
      }
    );
    if (error || !data?.orderId) {
      setStatus("error");
      setMessage("Impossible de créer la commande PayPal.");
      throw new Error("Création de commande PayPal échouée.");
    }
    return data.orderId as string;
  }

  async function handleApprove(data: { orderID: string }): Promise<void> {
    setStatus("processing");
    setMessage("");
    try {
      const { data: res, error } = await supabase.functions.invoke(
        "capture-paypal-order",
        { body: { orderId: data.orderID } }
      );
      if (error || !res) throw new Error("Erreur de capture.");
      if (res.status === "paid") {
        setStatus("success");
        setMessage("Merci pour votre générosité ! Votre don est confirmé.");
      } else if (res.status === "instrument_declined") {
        setStatus("idle");
        setMessage("Paiement refusé. Choisissez un autre moyen de paiement.");
      } else {
        setStatus("processing");
        setMessage("Paiement en cours de traitement…");
      }
    } catch {
      setStatus("error");
      setMessage("Une erreur est survenue lors du paiement.");
    }
  }

  const showSelector = stripeEnabled && paypalEnabled;

  return (
    <div
      id="don"
      className="p-6 md:p-8 bg-background-50 rounded-lg border border-background-200"
    >
      <div className="flex items-center gap-3">
        <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
          <i className="ri-heart-3-line text-2xl"></i>
        </span>
        <div>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
            Faire un don
          </h2>
          <p className="text-sm text-foreground-600">
            Chaque don compte pour faire avancer cette action.
          </p>
        </div>
      </div>

      <div className="mt-6">
        <p className="text-sm font-medium text-foreground-700 mb-2">
          Choisissez un montant
        </p>
        <div className="flex flex-wrap gap-2">
          {presetAmounts.map((value) => {
            const active = !custom.trim() && selected === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => {
                  setSelected(value);
                  setCustom("");
                }}
                className={`inline-flex items-center justify-center whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200 cursor-pointer ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 text-foreground-700 hover:bg-background-100"
                }`}
              >
                {value} €
              </button>
            );
          })}
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="1"
              step="1"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Autre"
              className="w-28 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
            />
            <span className="text-sm text-foreground-600">€</span>
          </div>
        </div>
      </div>

      {status === "success" ? (
        <div className="mt-6 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-700">
          <i className="ri-checkbox-circle-line mr-2"></i>
          {message}
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {showSelector && (
            <div className="inline-flex w-full rounded-full bg-background-100 p-1">
              <button
                type="button"
                onClick={() => setPaymentMethod("paypal")}
                className={`flex-1 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-all duration-200 cursor-pointer ${
                  paymentMethod === "paypal"
                    ? "bg-background-50 text-foreground-950 shadow-sm"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                <i className="ri-paypal-line"></i>
                PayPal
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod("card")}
                className={`flex-1 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-all duration-200 cursor-pointer ${
                  paymentMethod === "card"
                    ? "bg-background-50 text-foreground-950 shadow-sm"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                <i className="ri-bank-card-line"></i>
                CB / Carte bancaire
              </button>
            </div>
          )}

          {(paymentMethod === "card" || !showSelector) && stripeEnabled && (
            <button
              type="button"
              onClick={handleCard}
              disabled={!validAmount || status === "creating" || status === "processing"}
              className="w-full inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 px-6 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === "creating" ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Préparation…
                </>
              ) : (
                <>
                  <i className="ri-bank-card-line"></i>
                  Payer par carte ({formatEuro(amount)})
                </>
              )}
            </button>
          )}

          {(paymentMethod === "paypal" || !showSelector) &&
            paypalEnabled &&
            paypalClientId !== undefined &&
            (paypalOptions ? (
              <PayPalScriptProvider options={paypalOptions}>
                <PayPalButtons
                  style={{ layout: "vertical", color: "gold", shape: "pill", label: "donate" }}
                  createOrder={handleCreateOrder}
                  onApprove={handleApprove}
                  onCancel={() => {
                    setStatus("idle");
                    setMessage("Don annulé.");
                  }}
                  onError={() => {
                    setStatus("error");
                    setMessage("Une erreur PayPal est survenue.");
                  }}
                  forceReRender={[amount]}
                  disabled={!validAmount || status === "creating" || status === "processing"}
                />
              </PayPalScriptProvider>
            ) : (
              <p className="text-sm text-foreground-500">
                PayPal est temporairement indisponible.
              </p>
            ))}

          {!stripeEnabled && !paypalEnabled && (
            <p className="text-sm text-foreground-500">
              Le paiement en ligne est momentanément indisponible pour cette
              action.
            </p>
          )}

          {status === "error" && message && (
            <p className="text-sm text-accent-600">{message}</p>
          )}
          {status === "processing" && message && (
            <p className="text-sm text-foreground-600">{message}</p>
          )}
        </div>
      )}
    </div>
  );
}